import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as me from '../../../me'
import type * as variableOverrides from '../../../variable-overrides'

import type { Feature } from '../feature'
import * as gql from '../gql'

/**
 * Executes create/update requests for the route-bound scenario draft.
 */
export const submitEditOnRequest = store.effects.create(
  (
    feature: Feature,
    meFeature: me.Feature,
    variableOverridesFeature: variableOverrides.Feature,
  ) =>
    feature.actions.submitEdit.request.observe().pipe(
      rxjs.withLatestFrom(
        feature.selectors.editDialog.asObservable,
        meFeature.contexts.selectors.appContext.asObservable,
      ),
      rxjs.exhaustMap(([action, editDialog, appContext]) => {
        if (!editDialog) {
          return rxjs.of(
            feature.actions.submitEdit.cancellation.from(action).create({
              reason: 'No scenario draft is open',
              request: action.data,
            }),
          )
        }
        if (!appContext) {
          return rxjs.of(
            feature.actions.submitEdit.cancellation.from(action).create({
              reason: 'No app context available',
              request: action.data,
            }),
          )
        }

        return variableOverridesFeature.selectors.focusedValues.asObservable.pipe(
          rxjs.take(1),
          rxjs.switchMap(variableValues => {
            const draft = {
              ...editDialog.draft,
              variables: variableValues,
            }

            const request$ =
              editDialog.mode === 'create'
                ? gql.mutations.createScenario.executor({
                    ...appContext,
                    input: gql.inputs.scenarioInputs.TRANSFORMER.getGqlFrom(
                      draft,
                    ),
                  })
                : editDialog.scenarioId
                  ? gql.mutations.updateScenario.executor({
                      ...appContext,
                      scenarioId: editDialog.scenarioId,
                      updates:
                        gql.inputs.scenarioUpdates.TRANSFORMER.getGqlFrom(
                          draft,
                        ),
                    })
                  : undefined

            if (!request$) {
              return rxjs.of(
                feature.actions.submitEdit.cancellation.from(action).create({
                  reason: 'No scenario id available for update',
                  request: action.data,
                }),
              )
            }

            return request$.pipe(
              rxjs.map(() => editDialog.mode),
              rxjs.mergeMap(mode => {
                const successAction = feature.actions.submitEdit.success
                  .from(action)
                  .create({
                    request: action.data,
                    response: mode,
                  })

                if (mode === 'create') {
                  return rxjs.of(successAction)
                }

                return rxjs.of(
                  successAction,
                  feature.actions.refreshScenarios.request
                    .from('RouteBoundScenarios.SubmitEdit.Success')
                    .create(),
                )
              }),
              rxjs.catchError((error: Error) =>
                rxjs.of(
                  feature.actions.submitEdit.error.from(action).create({
                    error,
                    request: action.data,
                  }),
                ),
              ),
            )
          }),
        )
      }),
    ),
)
